/**
 * Quick cart drawer — opens on ATC, header cart control, and refreshes via
 * the Section Rendering API after Cart Ajax updates.
 */
(() => {
  const TAG = 'quick-cart';
  const OPEN_EVENTS = ['product_atc_via_form', 'product_atc_via_quick_add'];
  // Fired by add-to-cart surfaces when Shopify rejects an add. Refresh without opening.
  const REFRESH_EVENT = 'cart_refresh';
  const ROOT = window.Shopify?.routes?.root || '/';
  const enqueueCartMutation = (() => {
    if (window.themeEnqueueCartMutation) return window.themeEnqueueCartMutation;

    let queue = Promise.resolve();
    window.themeEnqueueCartMutation = (mutation) => {
      const next = queue.then(mutation, mutation);
      queue = next.catch(() => {});
      return next;
    };

    return window.themeEnqueueCartMutation;
  })();

  if (customElements.get(TAG)) return;

  const parseQuantity = (value) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 1) return 1;

    return parsed;
  };

  // The line's max (basket limit, stock or catalog rule) is rendered on the
  // input. Clamp to it so fast clicks or a typed value cannot overshoot before
  // the line re-renders with + disabled.
  const clampToMax = (input, value) => {
    const max = Number.parseInt(input.max, 10);

    return Number.isNaN(max) ? value : Math.min(value, max);
  };

  const swapChild = (liveRoot, incomingRoot, selector) => {
    const current = liveRoot.querySelector(selector);
    const nextEl = incomingRoot.querySelector(selector);

    if (current && nextEl) {
      current.replaceWith(nextEl);
      return;
    }

    if (current && !nextEl) current.remove();
  };

  // Everything in the summary except the wallets. Any DOM move of the live
  // `[data-cart-wallets]` node — reinsertion into the same parent included —
  // disconnects `shopify-accelerated-checkout-cart`, and Shopify does not
  // rehydrate it. So the parts around it are replaced where they stand and the
  // wallet node itself is never touched.
  const SUMMARY_PARTS = [
    '.cart-summary__heading',
    '.cart-summary__lines',
    '.cart-summary__tax-note',
    '.cart-summary__checkout',
    '.cart-summary__note',
  ];

  const patchSummaryAroundWallets = (liveSummary, nextSummary) => {
    // A part appearing or disappearing changes the order around the wallets,
    // which replacing in place cannot express. The caller falls back to a full
    // summary replace, which remounts and re-inits the wallets.
    const samePartsPresent = SUMMARY_PARTS.every(
      (selector) =>
        Boolean(liveSummary.querySelector(selector)) ===
        Boolean(nextSummary.querySelector(selector))
    );

    if (!samePartsPresent) return false;

    liveSummary.className = nextSummary.className;

    for (const selector of SUMMARY_PARTS) {
      const current = liveSummary.querySelector(selector);
      const incoming = nextSummary.querySelector(selector);

      if (current && incoming) current.replaceWith(incoming);
    }

    return true;
  };

  class QuickCart extends HTMLElement {
    #open = false;
    #openIntent = false;
    #previousOverflow = '';
    #refreshing = false;
    #giftPending = new Set();
    #refreshRequested = false;
    #refreshToken = 0;
    #opener = null;
    #pendingChanges = new Map();
    #refreshFocus = null;
    #inerted = [];
    #committed = new WeakMap();

    connectedCallback() {
      for (const eventName of OPEN_EVENTS) {
        document.addEventListener(eventName, this.#onAtc);
      }

      document.addEventListener(REFRESH_EVENT, this.#onCartRefresh);
      document.addEventListener('click', this.#onDocumentClick);
      this.#openPendingFromHead();
      // Keyboard handling is document-level so the trap holds even when focus
      // starts outside the drawer, e.g. tabbing in from the browser chrome.
      document.addEventListener('keydown', this.#onKeydown);
      this.addEventListener('free-gift:pending', this.#onFreeGiftPending);
      this.addEventListener('free-gift:settled', this.#onFreeGiftSettled);
      this.addEventListener('free-gift:updated', this.#onFreeGiftUpdated);
      this.addEventListener('click', this.#onClick);
      this.addEventListener('change', this.#onChange);
      this.addEventListener('submit', this.#onSubmit);
    }

    disconnectedCallback() {
      for (const eventName of OPEN_EVENTS) {
        document.removeEventListener(eventName, this.#onAtc);
      }

      document.removeEventListener(REFRESH_EVENT, this.#onCartRefresh);
      document.removeEventListener('click', this.#onDocumentClick);
      document.removeEventListener('keydown', this.#onKeydown);
      this.removeEventListener('free-gift:pending', this.#onFreeGiftPending);
      this.removeEventListener('free-gift:settled', this.#onFreeGiftSettled);
      this.removeEventListener('free-gift:updated', this.#onFreeGiftUpdated);
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('change', this.#onChange);
      this.removeEventListener('submit', this.#onSubmit);
      this.#unlockScroll();
      this.#releaseBackground();
    }

    #backdrop() {
      return this.querySelector('[data-quick-cart-backdrop]');
    }

    #panel() {
      return this.querySelector('[data-quick-cart-panel]');
    }

    #closeButton() {
      return this.querySelector('[data-quick-cart-close]');
    }

    #sectionId() {
      return this.getAttribute('data-section-id') || '';
    }

    hide({ restoreFocus = true } = {}) {
      this.#hide(restoreFocus);
    }

    #onAtc = () => {
      const quickAddDrawer = document.querySelector('quick-add-drawer');

      if (quickAddDrawer?.isOpen) {
        this.#opener =
          quickAddDrawer.opener instanceof HTMLElement
            ? quickAddDrawer.opener
            : null;
        quickAddDrawer.hide({ restoreFocus: false });
      }

      // Keep the original opener when an upsell is added from inside the drawer.
      if (!this.#opener && !this.#open && !this.#openIntent) {
        this.#opener = document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      }

      this.#openAfterRefresh();
    };

    #onDocumentClick = (event) => {
      const opener = event.target instanceof Element
        ? event.target.closest('[data-quick-cart-open]')
        : null;

      if (!opener) return;

      event.preventDefault();
      this.#openFrom(opener);
    };

    /**
     * A basket tap made before this file ran is held by the inline script in
     * layout/theme.liquid. Open for it now rather than lose the tap.
     */
    #openPendingFromHead() {
      const opener = window.themeQuickCartPendingOpener;

      if (!(opener instanceof Element)) return;

      window.themeQuickCartPendingOpener = null;
      this.#openFrom(opener);
    }

    #openFrom(opener) {
      const visibleHeaderCart = opener.closest('[data-mobile-menu]')
        ? document.querySelector('.header__mobile-row [data-quick-cart-open]')
        : opener;
      this.#opener = visibleHeaderCart instanceof HTMLElement ? visibleHeaderCart : null;
      // The drawer is server-rendered with the page and kept current after every
      // mutation, so a header click does not need another network request.
      this.#show();
    }

    #onSubmit = (event) => {
      const submitter = event.submitter;

      if (this.#refreshing || this.#giftPending.size > 0) {
        event.preventDefault();
        return;
      }

      // Only an explicit submitter other than the no-JS `update` fallback may post;
      // quantity is committed over Ajax, and implicit submission must never reach
      // the summary's checkout button.
      if (submitter && submitter.getAttribute('name') !== 'update') return;

      event.preventDefault();
    };

    #onFreeGiftPending = (event) => {
      this.#giftPending.add(event.detail?.id);
      this.#setBusy(true);
    };

    #onFreeGiftSettled = (event) => {
      this.#giftPending.delete(event.detail?.id);
      this.#setBusy(this.#refreshing);
    };

    #onFreeGiftUpdated = () => {
      this.#refreshRequested = true;
      this.#processChanges();
    };

    // The adoption step syncs the header count, so a silent refresh fixes both.
    #onCartRefresh = () => {
      this.#refreshRequested = true;
      this.#processChanges();
    };

    #onClick = (event) => {
      const close = event.target.closest('[data-quick-cart-close]');
      const backdrop = event.target.closest('[data-quick-cart-backdrop]');

      if (close || (backdrop && event.target === this.#backdrop())) {
        this.#hide();
        return;
      }

      const minus = event.target.closest('[data-cart-quantity-minus]');
      const plus = event.target.closest('[data-cart-quantity-plus]');
      const remove = event.target.closest('[data-cart-remove]');

      if (!minus && !plus && !remove) return;

      event.preventDefault();

      const line = event.target.closest('[data-cart-line-item]');
      if (!line) return;

      const key = line.getAttribute('data-line-key');
      if (!key) return;

      if (remove) {
        if (line.hasAttribute('data-promotional-gift')) {
          window.themeFreeGift?.suppressPromotion?.(
            line.getAttribute('data-gwp-promotion') || ''
          );
        }

        this.#changeLine(key, 0, '[data-cart-remove]');
        return;
      }

      const input = line.querySelector('[data-cart-quantity-input]');
      if (!(input instanceof HTMLInputElement)) return;

      const current = parseQuantity(input.value);
      const next = minus ? Math.max(1, current - 1) : clampToMax(input, current + 1);

      if (next === current) return;

      input.value = String(next);
      this.#changeLine(
        key,
        next,
        minus ? '[data-cart-quantity-minus]' : '[data-cart-quantity-plus]'
      );
    };

    #onChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.hasAttribute('data-cart-quantity-input')) return;

      const line = input.closest('[data-cart-line-item]');
      const key = line?.getAttribute('data-line-key');
      if (!key) return;

      const next = clampToMax(input, parseQuantity(input.value));
      input.value = String(next);

      // Enter commits on keydown and the field still fires change on blur.
      if (this.#committed.get(input) === next) {
        this.#committed.delete(input);
        return;
      }

      this.#committed.delete(input);
      this.#changeLine(key, next, '[data-cart-quantity-input]');
    };

    #onKeydown = (event) => {
      if (event.key === 'Escape' && (this.#open || this.#openIntent)) {
        this.#hide();
        return;
      }

      if (event.key === 'Enter') {
        this.#commitQuantityInput(event);
        return;
      }

      if (event.key !== 'Tab' || !this.#open) return;

      this.#trapTab(event);
    };

    #commitQuantityInput(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!this.contains(input)) return;
      if (!input.hasAttribute('data-cart-quantity-input')) return;

      // Implicit submission would send the shopper to checkout instead.
      event.preventDefault();

      const line = input.closest('[data-cart-line-item]');
      const key = line?.getAttribute('data-line-key');
      if (!key) return;

      const next = clampToMax(input, parseQuantity(input.value));
      input.value = String(next);
      this.#committed.set(input, next);
      this.#changeLine(key, next, '[data-cart-quantity-input]');
    }

    #trapTab(event) {
      const panel = this.#panel();
      if (!panel) return;

      const focusable = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      // Focus can start outside the panel, e.g. tabbing in from the browser chrome.
      if (!panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async #openAfterRefresh() {
      this.#openIntent = true;

      if (this.#refreshing) {
        this.#refreshRequested = true;
        return;
      }

      this.#refreshing = true;
      this.#setBusy(true);
      let releaseBusy = true;

      try {
        do {
          this.#refreshRequested = false;

          if (!(await this.#refreshWithRecovery())) {
            releaseBusy = false;
            window.location.reload();
            return;
          }

          if (this.#openIntent) this.#show();
        } while (this.#refreshRequested);
      } finally {
        this.#refreshing = false;

        if (releaseBusy) {
          this.#setBusy(false);
        }

        if (this.#pendingChanges.size > 0) {
          this.#processChanges();
        }
      }
    }

    #show() {
      if (this.#open) return;

      this.#open = true;
      this.#openIntent = true;
      this.setAttribute('data-open', 'true');
      this.setAttribute('aria-hidden', 'false');
      this.#panel()?.setAttribute('aria-modal', 'true');
      this.#lockScroll();
      this.#holdBackground();
      this.#closeButton()?.focus();
    }

    #hide(restoreFocus = true) {
      this.#openIntent = false;

      if (!this.#open) return;

      this.#open = false;
      this.removeAttribute('data-open');
      this.setAttribute('aria-hidden', 'true');
      this.#panel()?.removeAttribute('aria-modal');
      this.#unlockScroll();
      this.#releaseBackground();

      const opener = this.#opener;
      this.#opener = null;

      if (restoreFocus && opener?.isConnected) {
        requestAnimationFrame(() => opener.focus());
      }
    }

    async #requestLineChange(key, quantity) {
      return enqueueCartMutation(async () => {
        const response = await fetch(`${ROOT}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: key, quantity }),
        });

        if (!response.ok) throw new Error('Cart change failed');

        return response.json();
      });
    }

    #changeLine(key, quantity, focusSelector) {
      this.#pendingChanges.set(key, { quantity, focusSelector });
      this.#processChanges();
    }

    async #processChanges() {
      if (this.#refreshing) return;

      this.#refreshing = true;
      this.#setBusy(true);
      let releaseBusy = true;

      try {
        do {
          while (this.#pendingChanges.size > 0) {
            const [nextKey, change] = this.#pendingChanges.entries().next().value;
            this.#pendingChanges.delete(nextKey);
            this.#refreshFocus = { key: nextKey, selector: change.focusSelector };

            const cart = await this.#requestLineChange(nextKey, change.quantity);
            this.#syncHeaderCount(cart.item_count);
          }

          this.#refreshRequested = false;

          if (!(await this.#refreshWithRecovery())) {
            releaseBusy = false;
            window.location.reload();
            return;
          }

          if (this.#openIntent) this.#show();
        } while (this.#pendingChanges.size > 0 || this.#refreshRequested);
      } catch (error) {
        this.#pendingChanges.clear();
        if (!(await this.#refreshWithRecovery())) {
          releaseBusy = false;
          window.location.reload();
        }
      } finally {
        this.#refreshing = false;

        if (releaseBusy) {
          this.#setBusy(false);
        }

        if (this.#pendingChanges.size > 0) {
          this.#processChanges();
        }
      }
    }

    async #refreshWithRecovery() {
      if (await this.#refresh()) return true;

      return this.#refresh();
    }

    #restoreRefreshFocus({ key, selector }) {
      const line = Array.from(this.querySelectorAll('[data-cart-line-item]')).find(
        (item) => item.getAttribute('data-line-key') === key
      );
      const target = line?.querySelector(selector);

      if (target instanceof HTMLElement && !target.matches(':disabled')) {
        target.focus();
        return;
      }

      this.#closeButton()?.focus();
    }

    async #refresh() {
      const sectionId = this.#sectionId();
      if (!sectionId) return false;

      const url = `${ROOT}?sections=${encodeURIComponent(sectionId)}`;
      // Later refreshes win, so an out-of-order response never restores old markup.
      const token = ++this.#refreshToken;

      try {
        // No JSON Accept header: it makes Shopify omit
        // `content_for_additional_checkout_buttons`, so the section comes back
        // with an empty wallets container and any summary built from it has no
        // express checkout buttons.
        const response = await fetch(url);

        if (!response.ok || token !== this.#refreshToken) return false;

        const data = await response.json();
        const html = data[sectionId];
        if (!html || token !== this.#refreshToken) return false;

        const template = document.createElement('template');
        template.innerHTML = html.trim();
        const next = template.content.querySelector('quick-cart');
        if (!next) return false;

        window.themeCarousel?.destroyAll?.(this);
        const initWallets = this.#adoptMarkup(next, sectionId);
        this.#setBusy(this.#refreshing);
        this.#hydrateInjectedContent({ initWallets });

        if (this.#open) {
          this.setAttribute('data-open', 'true');
          this.setAttribute('aria-hidden', 'false');

          if (this.#refreshFocus) {
            this.#restoreRefreshFocus(this.#refreshFocus);
          } else {
            this.#closeButton()?.focus();
          }
        }

        return true;
      } catch (error) {
        // Keep the current drawer if the section request fails.
        return false;
      } finally {
        if (token === this.#refreshToken) {
          this.#refreshFocus = null;
        }
      }
    }

    #adoptMarkup(next, sectionId) {
      const liveWallets = this.querySelector('[data-cart-wallets]');
      const nextSummary = next.querySelector('.cart-summary');
      const nextHasWallets = Boolean(nextSummary?.querySelector('[data-cart-wallets]'));

      this.setAttribute('data-section-id', next.getAttribute('data-section-id') || sectionId);
      this.setAttribute('data-item-count', next.getAttribute('data-item-count') || '0');
      this.#syncHeaderCount(next.getAttribute('data-item-count'));

      swapChild(this, next, 'free-gift-controller');
      swapChild(this, next, '[data-quick-cart-backdrop]');
      swapChild(this, next, '.quick-cart__chrome');
      swapChild(this, next, '[data-quick-cart-body]');

      const liveSummary = this.querySelector('.cart-summary');
      const panel = this.#panel();

      if (
        liveSummary &&
        nextSummary &&
        liveWallets instanceof HTMLElement &&
        nextHasWallets &&
        patchSummaryAroundWallets(liveSummary, nextSummary)
      ) {
        return false;
      }

      if (nextSummary && panel) {
        if (liveSummary) {
          liveSummary.replaceWith(nextSummary);
        } else {
          panel.append(nextSummary);
        }

        return nextHasWallets;
      }

      liveSummary?.remove();
      return false;
    }

    #hydrateInjectedContent({ initWallets = false } = {}) {
      requestAnimationFrame(() => {
        if (initWallets) {
          window.Shopify?.PaymentButton?.init?.();
        }

        window.themeCarousel?.evaluateAll?.(this);
      });
    }

    #setBusy(busy) {
      // A refresh that finishes while the gift is still reconciling must not
      // release the lock, so the two states are combined rather than overwritten.
      const locked = busy || this.#giftPending.size > 0;

      if (locked) {
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
      }

      const checkout = this.querySelector('[name="checkout"]');
      if (checkout instanceof HTMLButtonElement) {
        checkout.disabled = locked;
      }

      const wallets = this.querySelector('[data-cart-wallets]');
      if (wallets instanceof HTMLElement) {
        wallets.inert = locked;

        // Must be the literal "true": the stylesheet keys pointer-events off
        // [aria-disabled='true'], which an empty attribute never matches.
        if (locked) {
          wallets.setAttribute('aria-disabled', 'true');
        } else {
          wallets.removeAttribute('aria-disabled');
        }
      }
    }

    #syncHeaderCount(itemCount) {
      const count = Number(itemCount) || 0;

      for (const badge of document.querySelectorAll('[data-cart-count]')) {
        if (count > 0) {
          badge.hidden = false;
          badge.textContent = String(count);
        } else {
          badge.hidden = true;
          badge.textContent = '';
        }
      }
    }

    #holdBackground() {
      for (const node of document.body.children) {
        if (node === this || node.contains(this)) continue;
        if (node.hasAttribute('inert')) continue;

        node.setAttribute('inert', '');
        this.#inerted.push(node);
      }
    }

    #releaseBackground() {
      for (const node of this.#inerted) {
        node.removeAttribute('inert');
      }

      this.#inerted = [];
    }

    #lockScroll() {
      this.#previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }

    #unlockScroll() {
      document.documentElement.style.overflow = this.#previousOverflow;
      this.#previousOverflow = '';
    }
  }

  customElements.define(TAG, QuickCart);
})();
